<template>
  <component :is="pageThemeComponent" class="g-container">
    <DelayHydration>
      <LazyPartialsBreadcrumbsV2
        v-if="useNewNavigation && !useFakeModal && pageResponse?.breadcrumbs"
        :breadcrumbs="pageResponse?.breadcrumbs"
      />
    </DelayHydration>

    <DelayHydration>
      <LazyFakeModalTop v-if="useFakeModal || !useNewNavigation" :show="showFakeModal" :is-car-object-page="isCarObjectPage" />
    </DelayHydration>

    <LazyRenderBlocks v-if="pageResponse?.blocks" :blocks="pageResponse?.blocks" />
  </component>
</template>

<script setup lang="ts">
import { usePageContentStore } from '~/stores/pageContent';
import { useSiteContentStore } from '~/stores/siteContent';
import type { PageContentQuery } from '~/types/generated-types';
import { capitalizeFirstLetter } from '~/helpers/string.helper';
import { useVehicleContext } from '~/stores/vehicleContext';
import { useTireSizeContextStore } from '~/stores/tireSizeContext';

const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();
const { setMetaData } = usePageContent();
const { systemText } = useSystemText();
const route = useRoute();
const pageContentStore = usePageContentStore();
const siteContentStore = useSiteContentStore();
const vehicleContext = useVehicleContext();
const tireSizeContext = useTireSizeContextStore();
const { loadCar } = useCar();

defineProps({
  useFakeModal: {
    type: Boolean,
    default: false,
  },
});

const schema = ref();
const showFakeModal = ref<boolean>(false);
const errorMessage = siteContentStore.systemTextLowerCasedKeys.get('ErrorPageNotFoundMessage'.toLowerCase()) ?? '';
const filter = useFilter();

const pageThemeComponent = shallowRef(
  defineAsyncComponent(
    () => import(`@/sites/${config.public.siteTheme}/pageThemes/${capitalizeFirstLetter(pageContentStore.pageTheme || 'bilia')}.vue`)
  )
);

const useNewNavigation = computed(() => {
  if (config.public.withNavigationPanel) {
    return true;
  } else {
    return false;
  }
});

const setScreenReaderData = () => {
  const screenReaderElement = document.getElementById('screenReaderAnnouncer');

  if (!screenReaderElement) return;

  screenReaderElement.textContent = `${systemText('WCAG_NewSiteLoaded')}, ${pageResponse.value?.title ?? pageContentStore.metadata?.title}`;
};

const preventRequest = (): boolean => {
  if (route.path.endsWith('svg')) {
    return true;
  }

  return false;
};

const isPageContentAlreadyFetched = (): boolean => {
  if (preventRequest()) {
    return true;
  }

  //Always reload page
  return false;
};

const fetchPage = (fetchRoute: string): Promise<PageContentQuery> => {
  return nuxtApp.$pageContentRepository.fetchPageContent(
    {
      route: fetchRoute,
      host: siteContentStore.host,
      context: '',
      previewindex: config.public.previewIndex,
      indexPrefix: siteContentStore.indexPrefix || config.public.indexPrefix,
      siteId: siteContentStore.siteId || config.public.siteId,
      registrationNumber: (route.query.regnr
        ? route.query.regnr
        : vehicleContext.vehicleInformation?.registrationNumber ?? undefined) as any,
      isTransportVehicle: (route.query.transportVehicle
        ? !!route.query.transportVehicle
        : tireSizeContext._transportVehicle && !tireSizeContext.hasTransportVehicleButSetNoTransport.setNoTransport
        ? true
        : undefined) as boolean,
      shouldFetchProductVariations: import.meta.server
        ? true
        : (!vehicleContext.vehicleInformation || tireSizeContext.hasManualTireSize) ?? false,
      languageCode: siteContentStore.languageCode || '',
    },
    'cache-first'
  );
};

const handleError = (statusCode: number, message: string): void => {
  const error = {
    message: message,
    statusCode: statusCode,
  };

  let isValidError = true;

  if (route.path.indexOf('_nuxt') >= 0) isValidError = false;
  if (route.path.endsWith('svg')) isValidError = false;

  if (isValidError) {
    if (import.meta.client) {
      showError(error);
    } else {
      throw createError(error);
    }
  }
};

const { data: pageResponse, refresh } = await useAsyncData(
  `pageContent-${route.fullPath.split('#').at(0)}`,
  async () => {
    if (!isPageContentAlreadyFetched()) {
      pageContentStore.setPending(true);
      const res = await fetchPage(route.path);

      if (res && res.pageContent) {
        pageContentStore.changePageContent({
          ...res.pageContent,
          blocks: [],
          pageInstance: { ...res.pageContent.pageInstance, schema: null },
        } as any);
      }

      if (!res || !res.pageContent) {
        handleError(404, errorMessage || 'Page not found');
      }

      if (import.meta.server) {
        schema.value =
          res.pageContent?.pageInstance?.schema?.map((s) => ({
            type: 'application/ld+json',
            innerHTML: s,
          })) ?? [];
      }

      pageContentStore.setPending(false);
      return res;
    }
  },
  {
    transform: (res) => {
      return {
        blocks: res?.pageContent?.blocks ?? [],
        pageTheme: res?.pageContent?.pageInstance?.pageTheme || 'bilia',
        title: res?.pageContent?.pageInstance?.metadata?.title ?? '',
        breadcrumbs: res?.pageContent?.pageInstance?.breadcrumbs ?? undefined,
        route: res?.pageContent?.route ?? '',
      };
    },
    getCachedData: undefined,
  }
);

callOnce(() => {
  if (import.meta.server && schema.value) {
    useHead({
      script: schema.value,
    });
  }
});

watch(
  () => vehicleContext.vehicleInformation?.registrationNumber,
  async (registrationNumber) => {
    if (pageContentStore.pageType !== 'ProductDetailPage') return;

    const router = useRouter();
    const query = { ...route.query };

    if (registrationNumber) {
      query.regnr = registrationNumber;
    } else {
      delete query.regnr;
    }

    await router.replace({ query });
    await nextTick();
    refresh();
  }
);

if (pageResponse) {
  if (pageResponse.value) {
    const { useHistoryHelperStore } = await import('~/stores/historyHelper');

    if (filter.urlWithFilters.value.includes('?')) useHistoryHelperStore().changeHistory(filter.urlWithFilters.value);
    else useHistoryHelperStore().changeHistory(pageResponse.value.route ?? '');

    if (!route.query.returnUrl) {
      const { useFormMetaDataStore } = await import('~/stores/formMetaData');
      useFormMetaDataStore().resetState();
    }

    // For setting correct canonical url for product pages
    let productPageUrl: string | null = null;
    if (pageResponse.value?.blocks && pageResponse.value?.blocks?.length) {
      if (pageResponse.value?.blocks?.at(0) && pageResponse.value?.blocks?.at(0)?.__typename === 'ProductHeadBlock') {
        // @ts-expect-error - Blocks are not typed
        productPageUrl = pageResponse.value?.blocks?.at(0)?.product?.canonicalUrl ?? null;
      }
    }

    setMetaData(productPageUrl);
    await loadCar();

    if (import.meta.client) setScreenReaderData();
  } else {
    handleError(404, errorMessage || 'Page not found');
  }
}

watch(pageResponse, () => {
  const pageTheme = pageResponse.value?.pageTheme || 'bilia';
  if (pageTheme) {
    pageThemeComponent.value = defineAsyncComponent(
      () => import(`@/sites/${config.public.siteTheme}/pageThemes/${capitalizeFirstLetter(pageTheme)}.vue`)
    );
  }
});

const isCarObjectPage = computed(() => !!pageContentStore.car);

if (!!route.query.returnUrl && route.query.returnUrl !== 'false') showFakeModal.value = true;

const events = ['mousemove', 'scroll', 'keydown', 'click', 'touchstart', 'wheel'];
const c = new AbortController();
let timer: NodeJS.Timeout;

onNuxtReady(() => {
  events.forEach((e) => {
    window.addEventListener(e, initGtm, {
      capture: true,
      once: true,
      passive: true,
      signal: c.signal,
    });
  });

  timer = setTimeout(() => {
    initGtm();
  }, 5500);
});

const cleanup = () => {
  events.forEach((e) => window.removeEventListener(e, initGtm));
  c.abort();
  clearTimeout(timer);
};

onBeforeUnmount(() => {
  cleanup();
});

const initGtm = () => {
  cleanup();

  if (config.public.googleTagManagerId && config.public.googleTagManagerId.length) {
    if (import.meta.client && window) {
      window.dataLayer = window?.dataLayer || [];
      useHead({
        script: [
          {
            type: 'text/javascript',
            innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${config.public.googleTagManagerId}');`,
          },
        ],
        noscript: [
          {
            tagPosition: 'bodyOpen',
            innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.public.googleTagManagerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          },
        ],
      });
    }
  }
};
</script>
